import { useEffect, useRef } from 'react'
import { arrowColor, arrows, plumeColor, plumeRibbon, puffAt } from '../main-ui/common/hunt-wind/thermals/model'
import { sample } from '../../lib/thermals/day'

const ARROWS_SRC = 'thermals-arrows-src'
const ARROWS_HALO = 'thermals-arrows-halo'
const ARROWS_LAYER = 'thermals-arrows'
const CANVAS_SRC = 'thermals-canvas-src'
const CANVAS_LAYER = 'thermals-canvas'

const M_PER_DEG_LAT = 111320
// The canvas's longer side. It is re-uploaded every frame while it animates, so it stays modest.
const CANVAS_PX = 1024
// Arrow length in metres per m/s of slope wind, and its bounds: typical slope winds are 0.5-3 m/s.
const ARROW_M_PER_MS = 45
const ARROW_MIN_M = 25
// Everything plays at HuntWind's speed-up, so air moves at its own pace: one second is two real minutes.
const TIME_SCALE = 120
// The combined wind as drifting specks: one per this many cells, each living a few seconds.
const CELLS_PER_SPECK = 6
const SPECK_LIFE_S = [1, 2.5]
const TRAIL_FADE = 0.3
// Scent puffs, as on the 2D map: one leaves the pin this often, gusting and meandering as it goes.
const PUFF_EVERY_MS = 150
const GUST = 0.25
const MEANDER = 0.35
const PUFF_MIN_M = 18
const PUFF_MAX_M = 120

const rgba = (hex, alpha) => {
  const n = parseInt(hex.slice(1), 16)
  return `rgba(${n >> 16}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`
}

const empty = { type: 'FeatureCollection', features: [] }

// Each slope-wind arrow as a shaft and a head, in metres around its cell centre, so it drapes on the terrain.
export function arrowFeatures(field, every) {
  const spacing = field.cellM * every
  return arrows(field, every, []).map(a => {
    const mPerDegLng = M_PER_DEG_LAT * Math.cos(a.lat * Math.PI / 180)
    const length = Math.min(spacing * 0.9, Math.max(ARROW_MIN_M, a.ms * ARROW_M_PER_MS))
    const ux = a.u / a.ms, uy = a.v / a.ms
    const at = (dx, dy) => [a.lng + dx / mPerDegLng, a.lat + dy / M_PER_DEG_LAT]
    const tail = at(-ux * length / 2, -uy * length / 2), tip = at(ux * length / 2, uy * length / 2)
    const head = length * 0.35, angle = Math.atan2(uy, ux)
    const barb = turn => at(ux * length / 2 - head * Math.cos(angle + turn), uy * length / 2 - head * Math.sin(angle + turn))
    return {
      type: 'Feature',
      properties: { color: arrowColor(a), alpha: Math.min(0.95, 0.45 + a.ms / 3) },
      geometry: { type: 'MultiLineString', coordinates: [[tail, tip], [barb(0.5), tip, barb(-0.5)]] }
    }
  })
}

// The pin's thermals on the 3D map: slope-wind arrows as lines on the terrain, and one animated canvas over the
// square with the scent plume's ribbon, the combined wind drifting across it, and scent puffs riding the plume.
export default function ThermalsOverlay3D({ map, field, every, opacity, show }) {
  const live = useRef({ field, show })
  live.current = { field, show }
  const canvasRef = useRef(null)
  const coordsRef = useRef(null)

  // Layers come and go with the field; the style reload after a basemap switch wipes them, so they are re-added.
  useEffect(() => {
    if (!map) return undefined
    if (!field) {
      remove(map)
      return undefined
    }
    const { west, south, east, north } = field.bounds
    const coordinates = [[west, north], [east, north], [east, south], [west, south]]
    const wideM = (east - west) * M_PER_DEG_LAT * Math.cos((north + south) / 2 * Math.PI / 180)
    const tallM = (north - south) * M_PER_DEG_LAT
    const k = CANVAS_PX / Math.max(wideM, tallM)
    if (!canvasRef.current) canvasRef.current = document.createElement('canvas')
    const canvas = canvasRef.current
    const w = Math.max(1, Math.round(wideM * k)), h = Math.max(1, Math.round(tallM * k))
    if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h }

    const add = () => {
      try {
        if (!map.getSource(CANVAS_SRC)) {
          map.addSource(CANVAS_SRC, { type: 'canvas', canvas, coordinates, animate: true })
          map.addLayer({ id: CANVAS_LAYER, type: 'raster', source: CANVAS_SRC, paint: { 'raster-fade-duration': 0 } })
        } else if (JSON.stringify(coordsRef.current) !== JSON.stringify(coordinates)) {
          map.getSource(CANVAS_SRC).setCoordinates(coordinates)
        }
        coordsRef.current = coordinates
        if (!map.getSource(ARROWS_SRC)) {
          map.addSource(ARROWS_SRC, { type: 'geojson', data: empty })
          map.addLayer({
            id: ARROWS_HALO, type: 'line', source: ARROWS_SRC,
            layout: { 'line-cap': 'round', 'line-join': 'round' },
            paint: { 'line-color': '#000', 'line-width': 4, 'line-opacity': 0.5 }
          })
          map.addLayer({
            id: ARROWS_LAYER, type: 'line', source: ARROWS_SRC,
            layout: { 'line-cap': 'round', 'line-join': 'round' },
            paint: { 'line-color': ['get', 'color'], 'line-width': 2 }
          })
        }
        map.getSource(ARROWS_SRC).setData(show.arrows ? { type: 'FeatureCollection', features: arrowFeatures(field, every) } : empty)
        paint(map, opacity)
      } catch (err) {
        console.error('thermals overlay:', err)
      }
    }
    if (map.isStyleLoaded()) add()
    else map.once('idle', add)
    const onStyle = () => { coordsRef.current = null; add() }
    map.on('style.load', onStyle)
    return () => { map.off('style.load', onStyle); map.off('idle', add) }
  }, [map, field, every, show.arrows]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (map) paint(map, opacity) }, [map, opacity])

  useEffect(() => () => { if (map) remove(map) }, [map])

  // The animation reads the latest field each tick, so scrubbing hours keeps the specks and puffs in flight.
  useEffect(() => {
    if (!field) return undefined
    const trail = document.createElement('canvas')
    let specks = [], puffs = [], then = 0, last = 0, frame = 0, fieldWas = null, ribbon = []
    const tick = time => {
      frame = requestAnimationFrame(tick)
      const canvas = canvasRef.current
      const { field: f, show: s } = live.current
      if (!canvas || !f) return
      const W = canvas.width, H = canvas.height
      if (trail.width !== W || trail.height !== H) { trail.width = W; trail.height = H; specks = [] }
      const dt = then ? Math.min(100, time - then) / 1000 : 0
      then = time
      const { west, south, east, north } = f.bounds
      const px = ll => ({ x: (ll.lng - west) / (east - west) * W, y: (north - ll.lat) / (north - south) * H })
      const pxPerM = W / ((east - west) * M_PER_DEG_LAT * Math.cos((north + south) / 2 * Math.PI / 180))
      if (f !== fieldWas) {
        fieldWas = f
        ribbon = f.plume && f.plume.points && f.plume.points.length > 1 ? plumeRibbon(f.plume, []) : []
      }

      // The combined wind: specks advected through cu, cv, leaving fading trails.
      const tctx = trail.getContext('2d')
      tctx.globalCompositeOperation = 'destination-out'
      tctx.fillStyle = `rgba(0, 0, 0, ${TRAIL_FADE})`
      tctx.fillRect(0, 0, W, H)
      tctx.globalCompositeOperation = 'source-over'
      if (s.flow && f.cu && f.cv) {
        const want = Math.round(f.cols * f.rows / CELLS_PER_SPECK)
        while (specks.length < want) specks.push(spawn(f, Math.random() * SPECK_LIFE_S[1]))
        if (specks.length > want) specks.length = want
        const cellPx = W / f.cols
        tctx.strokeStyle = 'rgba(255, 255, 255, 0.7)'
        tctx.lineWidth = Math.max(1, cellPx / 12)
        tctx.lineCap = 'round'
        tctx.beginPath()
        specks.forEach((p, i) => {
          const u = sample(f.cu, f.cols, f.rows, p.x - 0.5, p.y - 0.5)
          const v = sample(f.cv, f.cols, f.rows, p.x - 0.5, p.y - 0.5)
          const step = dt * TIME_SCALE / f.cellM
          const nx = p.x + u * step, ny = p.y - v * step
          tctx.moveTo(p.x * cellPx, p.y * cellPx)
          tctx.lineTo(nx * cellPx, ny * cellPx)
          p.x = nx; p.y = ny; p.age += dt
          if (p.age > p.life || nx < 0 || ny < 0 || nx > f.cols || ny > f.rows) specks[i] = spawn(f, 0)
        })
        tctx.stroke()
      } else if (specks.length) {
        specks = []
      }

      const ctx = canvas.getContext('2d')
      ctx.clearRect(0, 0, W, H)

      // The plume's ribbon, fading as the scent thins, under the specks.
      if (s.plume && ribbon.length > 1) {
        for (let i = 0; i < ribbon.length - 1; i++) {
          const a = ribbon[i], b = ribbon[i + 1]
          const quad = [a.left, b.left, b.right, a.right].map(px)
          ctx.fillStyle = rgba(plumeColor(a.status), 0.12 + 0.25 * a.strength)
          ctx.beginPath()
          ctx.moveTo(quad[0].x, quad[0].y)
          quad.slice(1).forEach(q => ctx.lineTo(q.x, q.y))
          ctx.closePath()
          ctx.fill()
        }
      }
      ctx.drawImage(trail, 0, 0)

      // Scent puffs leaving the pin and riding the plume at the air's speed there.
      if (s.plume && ribbon.length > 1) {
        const end = ribbon[ribbon.length - 1].seconds
        if (!last || time - last >= PUFF_EVERY_MS) {
          last = time
          puffs.push({ born: time, s: 0, side: (Math.random() * 2 - 1) * 0.8, phase: Math.random() * 2 * Math.PI, rate: 1 + Math.random() * 2 })
        }
        puffs.forEach(p => {
          const age = (time - p.born) / 1000
          p.s += dt * TIME_SCALE * (1 + GUST * Math.sin(p.phase + age * p.rate * 1.7))
        })
        puffs = puffs.filter(p => p.s < end)
        puffs.forEach(p => {
          const age = (time - p.born) / 1000
          const share = end > 0 ? p.s / end : 1
          const side = Math.max(-1, Math.min(1, p.side + MEANDER * Math.sin(p.phase + age * p.rate)))
          const at = puffAt(ribbon, share, side * Math.sqrt(share), [])
          if (!at) return
          const c = px(at), e = px(at.edge)
          const radius = Math.max(PUFF_MIN_M * pxPerM, Math.min(PUFF_MAX_M * pxPerM, Math.hypot(e.x - c.x, e.y - c.y) * 0.6))
          const alpha = Math.min(1, (time - p.born) / 300, (1 - share) * 5) * (0.25 + 0.6 * at.strength)
          const fill = ctx.createRadialGradient(c.x, c.y, 0, c.x, c.y, radius)
          fill.addColorStop(0, rgba(plumeColor(at.status), alpha))
          fill.addColorStop(0.4, rgba(plumeColor(at.status), alpha * 0.8))
          fill.addColorStop(1, rgba(plumeColor(at.status), 0))
          ctx.fillStyle = fill
          ctx.beginPath()
          ctx.arc(c.x, c.y, radius, 0, 2 * Math.PI)
          ctx.fill()
        })
      } else {
        puffs = []
      }
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [!!field]) // eslint-disable-line react-hooks/exhaustive-deps

  return null
}

const spawn = (f, age) => ({
  x: Math.random() * f.cols,
  y: Math.random() * f.rows,
  age,
  life: SPECK_LIFE_S[0] + Math.random() * (SPECK_LIFE_S[1] - SPECK_LIFE_S[0])
})

function paint(map, opacity) {
  const k = Math.max(0, Math.min(1, opacity / 100))
  try {
    if (map.getLayer(CANVAS_LAYER)) map.setPaintProperty(CANVAS_LAYER, 'raster-opacity', k)
    if (map.getLayer(ARROWS_LAYER)) map.setPaintProperty(ARROWS_LAYER, 'line-opacity', ['*', k, ['get', 'alpha']])
    if (map.getLayer(ARROWS_HALO)) map.setPaintProperty(ARROWS_HALO, 'line-opacity', 0.5 * k)
  } catch (_) {}
}

function remove(map) {
  [ARROWS_LAYER, ARROWS_HALO, CANVAS_LAYER].forEach(id => { try { if (map.getLayer(id)) map.removeLayer(id) } catch (_) {} });
  [ARROWS_SRC, CANVAS_SRC].forEach(id => { try { if (map.getSource(id)) map.removeSource(id) } catch (_) {} })
}
